import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Lock, LogOut, Plus, Pencil, Trash2, ArrowLeft, Save, Loader2,
  ImagePlus, Eye, EyeOff, FileText,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../api/apiUrl';
import { RichTextEditor } from '../components/RichTextEditor';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const resolveImg = (url) => (url && url.startsWith('/api') ? `${API_URL}${url}` : url);

const emptyForm = {
  title: '', category: '', excerpt: '', author: '', author_title: '',
  featured_image: null, content: '', published: true,
};

// ---- Discreet login screen ----
const EditorLogin = () => {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const res = await login(email.trim(), password);
    setLoading(false);
    if (!res.success) setError(res.error || 'Credenciales inválidas');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-notaria-primary to-notaria-secondary flex items-center justify-center px-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-8" data-testid="editor-login-card">
        <div className="flex flex-col items-center mb-6">
          <div className="w-14 h-14 rounded-full bg-notaria-primary/10 flex items-center justify-center mb-4">
            <Lock className="w-6 h-6 text-notaria-primary" />
          </div>
          <h1 className="text-2xl font-bold text-notaria-primary">Acceso Editor</h1>
          <p className="text-sm text-gray-500 mt-1">Panel de gestión del blog</p>
        </div>
        <form onSubmit={submit} className="space-y-4" data-testid="editor-login-form">
          <Input
            type="email" required placeholder="Correo electrónico"
            value={email} onChange={(e) => setEmail(e.target.value)}
            data-testid="editor-login-email"
          />
          <Input
            type="password" required placeholder="Contraseña"
            value={password} onChange={(e) => setPassword(e.target.value)}
            data-testid="editor-login-password"
          />
          {error && (
            <p className="text-sm text-red-600" data-testid="editor-login-error">{error}</p>
          )}
          <Button
            type="submit" disabled={loading}
            className="w-full bg-notaria-primary hover:bg-notaria-secondary text-white"
            data-testid="editor-login-submit"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Ingresar'}
          </Button>
        </form>
      </div>
    </div>
  );
};

// ---- Article form (create/edit) ----
const ArticleForm = ({ initial, onCancel, onSaved }) => {
  const { authConfig } = useAuth();
  const [form, setForm] = useState(initial || emptyForm);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API_URL}/api/blog/upload-image`, fd, authConfig());
      set('featured_image', data.url);
      toast.success('Imagen subida');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al subir la imagen');
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (!form.title.trim()) return toast.error('El título es obligatorio');
    if (!form.content || form.content.replace(/<[^>]*>/g, '').trim().length === 0) {
      return toast.error('El contenido no puede estar vacío');
    }
    setSaving(true);
    try {
      if (form.id) {
        await axios.put(`${API_URL}/api/blog/posts/${form.id}`, form, authConfig());
        toast.success('Artículo actualizado');
      } else {
        await axios.post(`${API_URL}/api/blog/posts`, form, authConfig());
        toast.success('Artículo publicado');
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto" data-testid="article-form">
      <button onClick={onCancel} className="inline-flex items-center gap-2 text-notaria-secondary mb-6 hover:underline" data-testid="form-back-btn">
        <ArrowLeft className="w-4 h-4" /> Volver a la lista
      </button>
      <h2 className="text-2xl font-bold text-notaria-primary mb-6">
        {form.id ? 'Editar artículo' : 'Nuevo artículo'}
      </h2>

      <div className="space-y-5">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">Título *</label>
          <Input value={form.title} onChange={(e) => set('title', e.target.value)} placeholder="Título del artículo" data-testid="form-title" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Categoría</label>
            <Input value={form.category} onChange={(e) => set('category', e.target.value)} placeholder="Ej. Blockchain" data-testid="form-category" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Autor</label>
            <Input value={form.author} onChange={(e) => set('author', e.target.value)} placeholder="Nombre del autor" data-testid="form-author" />
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">Cargo del autor</label>
          <Input value={form.author_title} onChange={(e) => set('author_title', e.target.value)} placeholder="Ej. Notario Titular" data-testid="form-author-title" />
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">Resumen (extracto)</label>
          <textarea
            value={form.excerpt} onChange={(e) => set('excerpt', e.target.value)}
            rows={3} placeholder="Breve descripción que aparece en la tarjeta del blog"
            className="w-full border-2 border-gray-200 rounded-lg p-3 text-gray-700 focus:outline-none focus:border-notaria-secondary"
            data-testid="form-excerpt"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">Imagen destacada</label>
          <div className="flex items-center gap-4">
            <label className="inline-flex items-center gap-2 cursor-pointer bg-gray-100 hover:bg-gray-200 px-4 py-2 rounded-lg text-sm font-medium text-gray-700 transition-colors" data-testid="form-image-label">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}
              {uploading ? 'Subiendo...' : 'Subir imagen'}
              <input type="file" accept="image/*" className="hidden" onChange={uploadImage} data-testid="form-image-input" />
            </label>
            {form.featured_image && (
              <img src={resolveImg(form.featured_image)} alt="preview" className="h-16 w-24 object-cover rounded-md border" data-testid="form-image-preview" />
            )}
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">Contenido *</label>
          <RichTextEditor value={form.content} onChange={(v) => set('content', v)} />
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => set('published', !form.published)}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${form.published ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}
            data-testid="form-published-toggle"
          >
            {form.published ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
            {form.published ? 'Publicado' : 'Borrador (oculto)'}
          </button>
        </div>

        <div className="flex gap-3 pt-2">
          <Button onClick={save} disabled={saving} className="bg-notaria-primary hover:bg-notaria-secondary text-white" data-testid="form-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Guardar
          </Button>
          <Button onClick={onCancel} variant="outline" data-testid="form-cancel-btn">Cancelar</Button>
        </div>
      </div>
    </div>
  );
};

// ---- Dashboard ----
const EditorDashboard = () => {
  const { user, logout, authConfig } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list'); // 'list' | 'form'
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/blog/admin/posts`, authConfig());
      setPosts(data);
    } catch (err) {
      toast.error('No se pudieron cargar los artículos');
    } finally {
      setLoading(false);
    }
  }, [authConfig]);

  useEffect(() => { load(); }, [load]);

  const remove = async (post) => {
    if (!window.confirm(`¿Eliminar "${post.title}"? Esta acción no se puede deshacer.`)) return;
    try {
      await axios.delete(`${API_URL}/api/blog/posts/${post.id}`, authConfig());
      toast.success('Artículo eliminado');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar');
    }
  };

  const onSaved = () => { setView('list'); setEditing(null); load(); };

  if (view === 'form') {
    return (
      <div className="min-h-screen bg-gray-50 pt-10 pb-16 px-4">
        <ArticleForm initial={editing} onCancel={() => { setView('list'); setEditing(null); }} onSaved={onSaved} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-10 pb-16 px-4">
      <div className="max-w-5xl mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-notaria-primary">Gestión del Blog</h1>
            <p className="text-sm text-gray-500 mt-1">Sesión: {user?.email} · {user?.role}</p>
          </div>
          <div className="flex gap-3">
            <Button onClick={() => { setEditing(null); setView('form'); }} className="bg-notaria-primary hover:bg-notaria-secondary text-white" data-testid="new-article-btn">
              <Plus className="w-4 h-4 mr-2" /> Nuevo artículo
            </Button>
            <Button onClick={logout} variant="outline" data-testid="editor-logout-btn">
              <LogOut className="w-4 h-4 mr-2" /> Salir
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-notaria-secondary" /></div>
        ) : posts.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-2xl border-2 border-dashed border-gray-200" data-testid="empty-state">
            <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">Aún no hay artículos. Crea el primero.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="posts-list">
            {posts.map((post) => (
              <div key={post.id} className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 flex items-center gap-4 hover:shadow-md transition-shadow" data-testid={`post-row-${post.id}`}>
                <div className="h-16 w-24 rounded-md overflow-hidden bg-gray-100 flex-shrink-0 flex items-center justify-center">
                  {post.featured_image
                    ? <img src={resolveImg(post.featured_image)} alt={post.title} className="h-full w-full object-cover" />
                    : <FileText className="w-6 h-6 text-gray-300" />}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900 truncate">{post.title}</h3>
                  <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500 mt-1">
                    <span>{post.date}</span>
                    <span>·</span>
                    <span>{post.category}</span>
                    <span className={`px-2 py-0.5 rounded-full ${post.published ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {post.published ? 'Publicado' : 'Borrador'}
                    </span>
                  </div>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <button onClick={() => { setEditing(post); setView('form'); }} className="w-9 h-9 flex items-center justify-center rounded-md text-notaria-secondary hover:bg-notaria-secondary hover:text-white transition-colors" data-testid={`edit-btn-${post.id}`} title="Editar">
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button onClick={() => remove(post)} className="w-9 h-9 flex items-center justify-center rounded-md text-red-500 hover:bg-red-500 hover:text-white transition-colors" data-testid={`delete-btn-${post.id}`} title="Eliminar">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const Editor = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-notaria-secondary" />
      </div>
    );
  }

  const isEditor = user && (user.role === 'admin' || user.role === 'editor');
  return isEditor ? <EditorDashboard /> : <EditorLogin />;
};

export default Editor;
